import * as React from "react";
import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import Tooltip from '@mui/material/Tooltip';
import Alert from "@mui/material/Alert";
import StyledGrid, { FilterChip } from "../../../shared/components/StyledGrid";
import { useNavigate } from "react-router-dom";
import { useCloseInvoiceMutation, useGetCommunicationsMutation, useGetInvoicesStatusesQuery, usePrintCommunicationMutation, useSendCommunicationEmailMutation } from "../../../services/InsuranceService";
import useFetchWithMsal from "../../../utils/useFetchWithMsal";
import { LoadingPanel } from "../../../shared/components/LoadingPanel";
import { Button, Typography, styled, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { useSelector } from "react-redux";
import { TableFilter, assessmentClasses, emailStatuses, communicationTypes } from "./tableFilter/TableFilter";
import { naicMemberRoleCheck } from "../../../shared/utils/permissions";
import moment from "moment";
import { AddItemContainer, AddItemOption, AddItemOptionsContainer, MultiFieldContainer, StatusContainer, StatusItem, StatusTitle, StatusValue, StatusIcon } from "./CommunicationTab.styled";
import dayjs from "dayjs";
import { AlternateEmail, LocalPrintshop, Refresh, Done, Error, Pending, DoneAll, EventNote, Assessment } from "@mui/icons-material";
import { CommunicationPopups } from "./popups/CommunicationPopups";

const StyledButton = styled(Button)(({ theme, isfull }) => ({
  backgroundColor: isfull ? theme.palette.primary.main : "",
  color: isfull ? theme.palette.info.contrastText : theme.palette.primary.main,
  marginRight: '15px',
  svg: {
    color: isfull ? theme.palette.info.contrastText : theme.palette.primary.main,
  },
  '&.Mui-disabled': {
    color: '#fff',
    opacity: 0.5,
  }
}));

export const findIdByName = (arr, name) => {
  const found = arr?.find(item => item.name === name);
  return found ? found.id : null;
};

export const CommunicationTab = props => {
  const { isMemberTab, invoices } = props;
  const roleId = useSelector((state) => state?.user?.roleId);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedCommunicationNaicCode, setSelectedCommunicationNaicCode] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [rowData, setRowData] = useState({});
  const [sortingModel, setSortingModel] = useState();
  const [isAddItemDropdownVisible, setIsAddItemDropdownVisible] = useState(false);

  const [isShowClassAPopup, setIsShowClassAPopup] = useState(false);
  const [isShowClassBPopup, setIsShowClassBPopup] = useState(false);  

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailRepeatDialogOpen, setEmailRepeatDialogOpen] = useState(false);

  const { skip } = useFetchWithMsal();
  const [getCommunications, { data, isLoading }] = useGetCommunicationsMutation("", { skip });
  const [sendCommunicationEmail, {isLoading: isInvoiceEmailSending}] = useSendCommunicationEmailMutation("");

  const [printCommunication, { data: invoiceFile, isLoading: isInvoiceLoading }] = usePrintCommunicationMutation("")

  const [closeInvoice, { isLoading: closeInvoiceLoading }] = useCloseInvoiceMutation("");

  const navigate = useNavigate();

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(null);
  
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    setIsAddItemDropdownVisible(false);
  }, [isShowClassAPopup, isShowClassBPopup]);

  const handleCloseStatus = (id) => {
    setSelectedInvoiceId(id);
    setConfirmDialogOpen(true);
  };

  const handleConfirmClose = () => {
    closeInvoice({id: selectedInvoiceId});
    setConfirmDialogOpen(false);
  };

  const handleCancel = () => {
    setConfirmDialogOpen(false);
  };
  
  const onPopupClose = () => {
    setIsShowClassAPopup(false);
    setIsShowClassBPopup(false)
  };

  const renderTypePill = (raw) => {
    if(!raw) return <Box component="span" sx={{ fontSize: 12, color: 'gray', ml: 1.5 }}>N/A</Box>;
    const label = String(raw);
    const l = label.toLowerCase();
    let bg = '#e5e7eb';
    let fg = '#374151';
    let Icon = null;
    if(l === 'annual meeting notice') { bg = '#e0f2fe'; fg = '#075985'; Icon = EventNote; }
    else if(l === 'pre-assessment' || l === 'pre-assesment') { bg = '#fff4e5'; fg = '#8a5a14'; Icon = Assessment; }
    return (
      <Box component="span" sx={{
        display: 'inline-flex', alignItems: 'center', px: 1, borderRadius: '15px', fontSize: 12,
        fontWeight: 600, bgcolor: bg, color: fg, textTransform: 'capitalize', minWidth: 80,
        height: 22, lineHeight: 1, ml: 1.5
      }}>
        {Icon && <Icon sx={{ fontSize: 14, mr: 0.5 }} />}{label}
      </Box>
    );
  };

  const invoicesColumns = [
    {
      field: "naicCode",
      headerName: "NAIC Code",
      width: 75,
      editable: true,
    },
    {
      field: "memberName",
      headerName: "Member Name",
      width: 300,
      minWidth: 200,
      editable: true,
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      width: 200,
      editable: true,
      flex: 1,
    },
    {
      field: "type",
      headerName: "Type",
      minWidth: 100,
      width: 100,
      editable: true,
      flex: 0.5,
      renderCell: (params) => renderTypePill(params.row.type)
    },
    {
      field: "communicationDate",
      headerName: "Communication Date",
      width: 100,
      minWidth: 75,
      editable: true,
      valueGetter: (value) => 
        moment.utc(value).local().format("MM/DD/YYYY")
    },
    {
      field: "emailDate",
      headerName: "Email date",
      width: 100,
      editable: true,
      valueGetter: (value) =>
        value ? moment.utc(value).local().format("MM/DD/YYYY") : "N/A",
    },
    {
      field: "emailStatus",
      headerName: "Email status",
      width: 100,
      editable: true,
      align: "center",
      headerAlign: "center",
      valueGetter: (value) =>
        value === 'Sent' && <Done />,
      renderCell: (params) => (
        <MultiFieldContainer>
          {params?.row?.emailStatus === 'Sent' && 
            <Tooltip title="Sent">
              <DoneAll
                style={{ color: "#009900" }}
              />
            </Tooltip>
          }
          {params?.row?.emailStatus === 'Failed' && 
            <Tooltip title="Failed">
              <Error
                style={{ color: "#CC3333" }}
              />
            </Tooltip>
          }
          {params?.row?.emailStatus === 'Pending' && 
            <Tooltip title="Pending">
              <Pending
                style={{ color: "#CCCC00" }}
              />
            </Tooltip>
          }
          {params?.row?.emailStatus === 'Ready For Send' && 
          <Tooltip title="Ready For Send">
            <Done
              style={{ color: "#99CC99" }}
            />
          </Tooltip>
          }
          {/* {params?.row?.emailStatus === 'Pending' || params?.row?.emailStatus === 'Ready For Send'  && params?.row?.emailStatus} */}
        </MultiFieldContainer>
      ),
    },
    {
      field: "emailButton",
      headerName: "",
      width: 30,
      editable: true,
      renderCell: (params) => (
        <MultiFieldContainer>
          <AlternateEmail
            style={{ cursor: "pointer", color: "#003366" }}
            // onClick={() => handleSendSingleEmail(params?.row?.id)}
            onClick={
              params?.row?.emailStatus !== null && params?.row?.emailStatus !== 'Ready For Send'
                ? () => handleEmailRepeatConfirmOpen(params?.row?.id, params?.row?.naicCode)
                : () => handleSendSingleEmail(params?.row?.id, params?.row?.naicCode)
              }
          />
        </MultiFieldContainer>
      ),
    },
    {
      field: "printButton",
      headerName: "",
      width: 30,
      editable: true,
      renderCell: (params) => (
        <MultiFieldContainer>
          <LocalPrintshop
            style={{ cursor: "pointer", color: "#003366" }}
            onClick={() => handlePrintSingleInvoice(params?.row?.id)}
          />
        </MultiFieldContainer>
      ),
    }
  ];

  const {
    naicCode,
    memberName,
    status,
    assessmentDate,
    invoiceNumber,
    assessmentClass,
    emailStatus,
    communicationType,
    communicationDate,
    emailDate,
    email,
  } = useSelector(
    (state) => state.filter
  );

  const handleSendSingleEmail = async (id, naicCode) => {
    setEmailRepeatDialogOpen(false)
    try {
      const response = await sendCommunicationEmail({
        filter: null,
        items: [Number(id)],
      }).unwrap();
  
      setSnackbarMessage(`Communication for member with NAIC Code ${naicCode} sent successfully.`);
      setSnackbarSeverity("success");
    } catch (error) {
      console.log(error)
      setSnackbarMessage(error?.data?.Message);
      setSnackbarSeverity("error");
    }
    setSnackbarOpen(true);
  };

  const handlePrintSingleInvoice = async (id, naicCode) => {
    try {
      const response = await printCommunication({
        filter: null,
        items: [Number(id)],
      }).unwrap();
      
      setSnackbarMessage(`Communication for member with NAIC Code ${id} print process started successfully.`);
      setSnackbarSeverity("success");
    } catch (error) {
      setSnackbarMessage(error?.data?.Message);
      setSnackbarSeverity("error");
    }
    // setSnackbarOpen(true);
  };  

  const handleEmailConfirmOpen = () => {
    setEmailDialogOpen(true);
  };

  const handleEmailConfirmClose = () => {
    setEmailDialogOpen(false);
  };

  const handleEmailRepeatConfirmOpen = (id, naicCode) => {
    setSelectedInvoiceId(id);
    setSelectedCommunicationNaicCode(naicCode)
    setEmailRepeatDialogOpen(true);
  };

  const handleEmailRepeatConfirmClose = () => {
    setEmailRepeatDialogOpen(false);
  };

  const handleEmailSend = async () => {
    setEmailDialogOpen(false);
    try {
      const response = await sendCommunicationEmail({
        filter:
          selectedRows.length === rows.length || selectedRows.length === 0
            ? {
                naicCode: naicCode,
                memberName: memberName,
                email: email,
                type: findIdByName(communicationTypes, communicationType),
                communicationDate: communicationDate,
                emailDate: emailDate,
                emailStatus: findIdByName(emailStatuses, emailStatus),
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              }
            : null,
        items: selectedRows.length !== rows.length && selectedRows.length !== 0 ? selectedRows : null,
      }).unwrap();

      setSnackbarMessage(`Send communications for ${response} selected members is started.`);
      setSnackbarSeverity("success");
    } catch (error) {
      console.log(error)
      setSnackbarMessage(error?.data?.Message);
      setSnackbarSeverity("error");
    }
    setSnackbarOpen(true);
  };

  const { data: invoicesStatuses } = useGetInvoicesStatusesQuery("");

  const [selectedRows, setSelectedRows] = useState([]);

  const [selectAll, setSelectAll] = useState(false);

  const [rows, setRows] = useState([]);

  useEffect(() => {
    const body = {
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      orderBy: sortingModel?.field || "",
      orderByDirection: sortingModel?.sort || "",
      naicCode: naicCode,
      memberName: memberName,
      email: email,
      type: findIdByName(communicationTypes, communicationType),
      communicationDate: communicationDate,
      emailDate: emailDate,
      emailStatus: findIdByName(emailStatuses, emailStatus),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  
    const timer = setTimeout(() => {
      if (!confirmDialogOpen && !isMemberTab) {
        getCommunications(body);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    sortingModel?.field,
    sortingModel?.sort,
    confirmDialogOpen
  ]);

  useEffect(() => {
    if(selectedRows?.length === rows?.length && rows?.length) {
      setSelectAll(true);
    }
  }, [selectedRows]);

  // useEffect(() => {
  //   setSelectedRows([])
  //   console.log('test');
  // }, [naicCode, status, assessmentDate, invoiceNumber, assessmentClass]);

  const extractIds = (data) => {
    return data?.map(item => item.id);
  }

  useEffect(() => {
    setRows(isMemberTab ? invoices : data?.items);
    if(selectAll) {
      setSelectedRows([]);
      setSelectAll(false);
    }
  }, [isLoading, invoices, isMemberTab]);

  useEffect(() => {
    if (!isLoading) {
      if(isMemberTab) {
        if (invoices?.length !== totalCount) {
          setTotalCount(invoices?.length);
        }
      }
      else {
        if (data?.totalCount !== totalCount) {
          setTotalCount(data?.totalCount);
        }
      }
    }
  }, [isLoading, invoices, isMemberTab]);
  
  const fetchData = (value) => {
    const body = {
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      orderBy: sortingModel?.field || "",
      orderByDirection: sortingModel?.sort || "",
      naicCode: value ? value.naicCode : naicCode,
      memberName: value ? value.memberName : memberName,
      email: value ? value.email : email,
      type: value ? findIdByName(communicationTypes, value.communicationType) : findIdByName(communicationTypes, communicationType),
      communicationDate: value?.communicationDate
        ? new Date(value.communicationDate)
        : null,
      emailDate: value?.emailDate
        ? new Date(value.emailDate)
        : null,
      emailStatus: value ? findIdByName(emailStatuses, value.emailStatus) : findIdByName(emailStatuses, emailStatus),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    getCommunications(body);
  };

  const handlePaginationModelChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };

  const onRowClick = (e) => {
    setRowData(e.row);
    setIsPopupOpen(true);
  };

  const onApplyFilters = (value) => {
    setSelectedRows([])
    if (paginationModel.page) {
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    } else {
      fetchData(value);
    }
  };

  const handleSelectionChange = (newSelectedRows) => {
    setSelectedRows(newSelectedRows);
  };

  const sentInvoiceHandler = () => {
    printCommunication({
      "filter": selectedRows?.length === rows?.length ? {
        naicCode: naicCode,
        statusId: findIdByName(invoicesStatuses, status),
        assessmentDate: assessmentDate,
        invoiceNumber: invoiceNumber,
        assessmentClass: findIdByName(assessmentClasses, assessmentClass),
        emailStatus: findIdByName(emailStatuses, emailStatus),
        communicationType: findIdByName(communicationTypes, communicationType),
        communicationDate: communicationDate,
        emailDate: emailDate,
      } : null,
      "items": (selectedRows?.length !== rows?.length && selectedRows?.length !== 0) ? selectedRows : null
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={!isMemberTab && { height: "100%", width: "100%", margin: "50px auto 0 auto" }}>
      {(isLoading || isInvoiceEmailSending || isInvoiceLoading) && <LoadingPanel />}
      {!isMemberTab && <Box display={"flex"} sx={{ justifyContent: "space-between" }} mb={3}>
        <Typography variant="h1" color="primary.main" sx={{ marginLeft: "16px" }}>
          Communication
        </Typography>
        <Box display={"flex"}>
        {/* {naicMemberRoleCheck(roleId) && <StyledButton
          variant="outlined"
          isfull
          onClick={() => sentInvoiceHandler()}
        >
          Print
        </StyledButton>} */}
        <Button
          variant="outlined"
          onClick={() => fetchData()}
          style={{ marginRight: '30px' }}
          startIcon={<Refresh />}
        >
          Refresh
        </Button>
        {naicMemberRoleCheck(roleId) && <AddItemContainer>
          <Button
            variant="outlined"
            onClick={() => setIsAddItemDropdownVisible(!isAddItemDropdownVisible)}
          >
            Add new
          </Button>
          {isAddItemDropdownVisible && <AddItemOptionsContainer>
            <AddItemOption onClick={() => setIsShowClassAPopup(true)}>Pre-assesment</AddItemOption>
            <AddItemOption onClick={() => setIsShowClassBPopup(true)}>Annual Meeting Notice</AddItemOption>
          </AddItemOptionsContainer>}
        </AddItemContainer>}
        {naicMemberRoleCheck(roleId) && (
          <StyledButton variant="outlined" isfull onClick={handleEmailConfirmOpen} disabled={selectedRows?.length === 0}>
            Email ({selectedRows?.length === rows?.length ? data?.totalCount : selectedRows?.length})
          </StyledButton>
        )}
          <TableFilter onApplyFilters={onApplyFilters} />
        </Box>
      </Box>}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          marginRight: "15px",
          marginBottom: "15px",
        }}
      >
        {naicCode && <FilterChip>NAIC Code: {naicCode}</FilterChip>}
        {memberName && <FilterChip>Member Name: {memberName}</FilterChip>}
        {email && <FilterChip>Email: {email}</FilterChip>}
        {emailStatus && <FilterChip>Email Status: {emailStatus}</FilterChip>}
        {emailDate && <FilterChip>Email Date: {emailDate}</FilterChip>}
        {communicationType && <FilterChip>Communication Type: {communicationType}</FilterChip>}
        {communicationDate && <FilterChip>Communication Date: {communicationDate}</FilterChip>}
      </Box>
      <StatusContainer>
        <StatusItem>
          <StatusValue>Summary:</StatusValue>
        </StatusItem>
        <StatusItem>
          <StatusIcon>
            <Done
              style={{ color: "#99CC99" }}
            />
          </StatusIcon>
          <StatusTitle>Ready For Send:</StatusTitle>
          <StatusValue>{data?.readyForSendCount}</StatusValue>
        </StatusItem>
        <StatusItem>
          <StatusIcon>
            <Pending
              style={{ color: "#CCCC00" }}
            />
          </StatusIcon>
          <StatusTitle>Pending:</StatusTitle>
          <StatusValue>{data?.pendingCount}</StatusValue>
        </StatusItem>
        <StatusItem>
          <StatusIcon>
            <DoneAll
              style={{ color: "#009900" }}
            />
          </StatusIcon>
          <StatusTitle>Sent:</StatusTitle>
          <StatusValue>{data?.sentCount}</StatusValue>
        </StatusItem>
        <StatusItem>
          <StatusIcon>
            <Error
              style={{ color: "#CC3333" }}
            />
          </StatusIcon>
          <StatusTitle>Failed:</StatusTitle>
          <StatusValue>{data?.failedCount}</StatusValue>
        </StatusItem>
      </StatusContainer>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <StyledGrid
        data={rows}
        width="100%"
        columns={invoicesColumns}
        rowCount={totalCount}
        withFilter={!isMemberTab}
        onSortingModelChange={(model) => setSortingModel(model[0])}
        paginationModel={paginationModel}
        setPaginationModel={handlePaginationModelChange}
        onRowClick={onRowClick}
        withCheckboxSelection={(naicMemberRoleCheck(roleId) && !isMemberTab)}
        checkboxSelection
        selectionModel={selectedRows}
        onRowSelectionModelChange={handleSelectionChange}
        selectAll
      />
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancel}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        disableEnforceFocus
      >
        <DialogTitle id="confirm-dialog-title">Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Do you really want to set the status to close value?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmClose} color="error" autoFocus>
            Void
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={emailDialogOpen}
        onClose={handleEmailConfirmClose}
        aria-labelledby="email-dialog-title"
        aria-describedby="email-dialog-description"
        disableEnforceFocus
      >
        <DialogContent>
          <DialogContentText id="email-dialog-description">
            You have selected {selectedRows?.length === rows?.length ? data?.totalCount : selectedRows?.length} communication for send.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEmailConfirmClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleEmailSend} color="success" autoFocus>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={emailRepeatDialogOpen}
        onClose={handleEmailRepeatConfirmClose}
        aria-labelledby="email-dialog-title"
        aria-describedby="email-dialog-description"
        disableEnforceFocus
      >
        <DialogContent>
          <DialogContentText id="email-dialog-description">
            This invoice has been already sent. Do you want to resend it?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEmailRepeatConfirmClose} color="primary">
            Cancel
          </Button>
          <Button onClick={() => handleSendSingleEmail(selectedInvoiceId, selectedCommunicationNaicCode)} color="success" autoFocus>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
      <CommunicationPopups
        classAPopupOpen={isShowClassAPopup}
        classBPopupOpen={isShowClassBPopup}
        open={isShowClassAPopup || isShowClassBPopup}
        onClose={onPopupClose}
        data={rowData}
      />
    </Box>
  );
};